import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Mail, PenSquare, RefreshCw, AlertTriangle, CheckSquare, Square } from 'lucide-react';
import { useAuth } from '../../App';
import { MAIL_API, mailAuth, FOLDERS, PANEL, getMailbox } from './mailUtils';
import { MailboxSwitch } from './MailboxSwitch';
import { MailThreadList } from './MailThreadList';
import { MailThreadView } from './MailThreadView';
import { MailComposer } from './MailComposer';
import { MailAlertsToggle } from './MailAlertsToggle';
import { MailFolderNav } from './MailFolderNav';
import { MailSearchBar } from './MailSearchBar';
import { MailBulkBar } from './MailBulkBar';
import { ShortcutsHelp } from './ShortcutsHelp';
import { useMailShortcuts } from './useMailShortcuts';

export default function MailPage() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const folder = params.get('folder') || 'inbox';
  const selected = params.get('thread');
  const activeQ = params.get('q') || '';
  const [q, setQ] = useState(activeQ);
  const [scope, setScope] = useState(params.get('scope') || 'all');
  const [status, setStatus] = useState(null);
  const [threads, setThreads] = useState([]);
  const [next, setNext] = useState(null);
  const [loading, setLoading] = useState(false);
  const [compose, setCompose] = useState(false);
  const [labels, setLabels] = useState([]);
  const [saved, setSaved] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [focusId, setFocusId] = useState(null);
  const [help, setHelp] = useState(false);
  const searchRef = useRef(null);
  const threadKeys = useRef(null);
  const [mailbox, setMailboxState] = useState(getMailbox());

  const loadMeta = useCallback(() => {
    axios.get(`${MAIL_API}/status`, mailAuth()).then(r => setStatus(r.data)).catch(() => setStatus({ ok: false, allowed: false }));
    axios.get(`${MAIL_API}/labels`, mailAuth()).then(r => setLabels(r.data.labels)).catch(() => {});
    axios.get(`${MAIL_API}/saved-searches`, mailAuth()).then(r => setSaved(r.data.searches)).catch(() => {});
  }, []);
  useEffect(() => { loadMeta(); }, [loadMeta]);

  // switching mailbox (My mail ↔ AP@ …) starts over in that inbox
  const switchMailbox = (email) => {
    setMailboxState(email);
    setStatus(null); setThreads([]); setNext(null); setSelectedIds(new Set()); setQ('');
    const p = new URLSearchParams(); p.set('folder', 'inbox'); setParams(p);
    loadMeta();
  };

  const load = useCallback(async (append = false) => {
    setLoading(true);
    try {
      const r = await axios.get(`${MAIL_API}/threads`, { ...mailAuth(), params: { folder, q: activeQ, scope, page_token: append ? next : '', limit: 30 } });
      setThreads(t => (append ? [...t, ...r.data.threads] : r.data.threads));
      setNext(r.data.next_page_token || null);
      if (!append) setSelectedIds(new Set());
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'object' ? d.message : d || 'Could not load mail');
    }
    setLoading(false);
  }, [folder, activeQ, scope, next, mailbox]);
  useEffect(() => { load(false); }, [folder, activeQ, scope, mailbox]);
  useEffect(() => { setQ(activeQ); }, [activeQ]);

  const setParam = (k, v) => { const p = new URLSearchParams(params); v ? p.set(k, v) : p.delete(k); setParams(p); };
  const runSearch = (text) => { const p = new URLSearchParams(params); text ? p.set('q', text) : p.delete('q'); p.set('scope', scope); p.delete('thread'); setParams(p); };
  const goFolder = (id) => { const p = new URLSearchParams(); p.set('folder', id); setQ(''); setParams(p); };

  const onChanged = (c) => {
    const leaves = c.removed || (folder === 'inbox' && c.in_inbox === false);
    setThreads(ts => (leaves ? ts.filter(t => t.id !== c.id) : ts.map(t => (t.id === c.id ? { ...t, ...c } : t))));
    if (c.refresh) load(false);
    if (c.removed) axios.get(`${MAIL_API}/status`, mailAuth()).then(r => setStatus(r.data)).catch(() => {});
  };
  const star = async (t) => {
    try {
      await axios.post(`${MAIL_API}/threads/${t.id}/modify`, t.starred ? { remove: ['STARRED'] } : { add: ['STARRED'] }, mailAuth());
      onChanged({ id: t.id, starred: !t.starred });
    } catch (e) { const d = e.response?.data?.detail; toast.error(typeof d === 'object' ? d.message : d || 'Could not star'); }
  };
  const toggleSelect = (id) => setSelectedIds(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const bulkDone = ({ what, ids }) => {
    const gone = ['archive', 'trash', 'move'].includes(what) && folder !== 'all';
    setThreads(ts => (gone ? ts.filter(t => !ids.includes(t.id)) : ts.map(t => (ids.includes(t.id) ? { ...t, unread: what === 'unread' ? true : what === 'read' ? false : t.unread, starred: what === 'star' ? true : t.starred } : t))));
    setSelectedIds(new Set());
    axios.get(`${MAIL_API}/status`, mailAuth()).then(r => setStatus(r.data)).catch(() => {});
  };
  const onLabelCreated = (l) => setLabels(ls => (ls.some(x => x.id === l.id) ? ls : [...ls, l].sort((a, b) => a.name.localeCompare(b.name))));

  // keyboard
  const idx = threads.findIndex(t => t.id === (focusId || selected));
  useMailShortcuts({
    next: () => { const t = threads[Math.min(idx + 1, threads.length - 1)]; if (t) { setFocusId(t.id); if (selected) setParam('thread', t.id); } },
    prev: () => { const t = threads[Math.max(idx - 1, 0)]; if (t) { setFocusId(t.id); if (selected) setParam('thread', t.id); } },
    open: () => { const t = threads[idx] || threads[0]; if (t) setParam('thread', t.id); },
    back: () => { if (help) setHelp(false); else if (selected) setParam('thread', ''); else setSelectedIds(new Set()); },
    select: () => { const t = threads[idx]; if (t) toggleSelect(t.id); },
    compose: () => status?.can_send && setCompose(true),
    search: () => searchRef.current?.focus(),
    help: () => setHelp(h => !h),
    goto: goFolder,
    thread: (k, ev) => (selected && threadKeys.current ? threadKeys.current(k, ev) : false),
  }, !!status?.allowed);

  if (status && !status.allowed) return <div className={`${PANEL} p-8 text-slate-700`} data-testid="mail-not-allowed">Mail in ops is for office, PM and dispatch roles.</div>;
  const folderLabel = folder.startsWith('label:') ? (labels.find(l => l.id === folder.slice(6))?.name || 'Label') : FOLDERS.find(f => f.id === folder)?.label;
  const allSelected = threads.length > 0 && threads.every(t => selectedIds.has(t.id));

  return (
    <div className="-m-3 lg:-m-6 p-3 lg:p-6 h-[calc(100vh-4.5rem)] min-h-[560px] flex flex-col ops-canvas" data-testid="mail-page">
      <div className="flex items-start gap-3 mb-3 flex-wrap">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">Mail</h1>
            {status?.ok && <span className="text-xs text-slate-600" data-testid="mail-status">{status.email} · {status.unread} unread{status.can_send ? '' : ' · read-only'}</span>}
            {status && !status.ok && status.allowed && <span className="text-xs text-amber-700 flex items-center gap-1" title={status.action} data-testid="mail-status-error"><AlertTriangle className="w-3.5 h-3.5" />{status.message}</span>}
            <MailAlertsToggle />
          </div>
          <MailboxSwitch onChange={switchMailbox} />
        </div>
        <div className="ml-auto flex items-start gap-2 w-full xl:w-auto xl:min-w-[640px]">
          <MailSearchBar q={q} setQ={setQ} onSearch={runSearch} onClear={() => runSearch('')} scope={scope} setScope={setScope} inFolder={folder} onSaved={s => setSaved(ss => [...ss, s].sort((a, b) => a.name.localeCompare(b.name)))} onHelp={() => setHelp(true)} inputRef={searchRef} />
          <button type="button" onClick={() => setCompose(true)} disabled={status && !status.can_send} className="px-4 py-2 rounded-xl bg-sky-600 hover:bg-sky-500 disabled:bg-slate-300 text-white text-sm font-bold flex items-center gap-2 shadow-md shadow-sky-900/20 shrink-0" data-testid="mail-compose-new"><PenSquare className="w-4 h-4" />Compose</button>
        </div>
      </div>

      <div className="flex-1 min-h-0 grid grid-cols-1 grid-rows-[auto_minmax(0,1fr)] lg:grid-rows-1 lg:grid-cols-[210px_minmax(300px,400px)_1fr] gap-3">
        <MailFolderNav folder={folder} q={activeQ} unread={status?.unread} labels={labels} savedSearches={saved} hidden={!!selected}
          onFolder={goFolder} onSavedSearch={s => { setScope(s.scope || 'all'); const p = new URLSearchParams(); p.set('folder', s.folder || 'all'); p.set('q', s.q); p.set('scope', s.scope || 'all'); setParams(p); }}
          onDeleteSaved={async s => { await axios.delete(`${MAIL_API}/saved-searches/${s.id}`, mailAuth()).catch(() => {}); setSaved(ss => ss.filter(x => x.id !== s.id)); }} onLabelCreated={onLabelCreated} />

        <section className={`${selected ? 'hidden lg:flex' : 'flex'} flex-col ${PANEL} overflow-hidden`} data-testid="mail-list-pane">
          <div className="px-3 py-2 border-b border-slate-200/60 flex items-center gap-2 text-xs text-slate-600 bg-white/40">
            {status?.can_send && <button onClick={() => setSelectedIds(allSelected ? new Set() : new Set(threads.map(t => t.id)))} className="p-0.5 text-slate-500 hover:text-slate-900" title="Select all" data-testid="mail-select-all">{allSelected ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}</button>}
            <span className="font-bold text-slate-700 uppercase tracking-wider truncate" data-testid="mail-list-title">{activeQ ? `Results for “${activeQ}”` : folderLabel}</span>
            <button onClick={() => load(false)} className="ml-auto p-1.5 rounded-lg hover:bg-slate-900/5" title="Refresh" data-testid="mail-refresh"><RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /></button>
          </div>
          {selectedIds.size > 0 && <MailBulkBar ids={[...selectedIds]} total={threads.length} onClear={() => setSelectedIds(new Set())} onSelectAll={() => setSelectedIds(new Set(threads.map(t => t.id)))} onDone={bulkDone} labels={labels} onLabelCreated={onLabelCreated} inInbox={folder === 'inbox' || folder === 'all' || !!activeQ} />}
          <div className="flex-1 overflow-y-auto">
            <MailThreadList threads={threads} selectedId={selected} loading={loading} onStar={status?.can_send ? star : null} selectable={!!status?.can_send} selectedIds={selectedIds} onToggleSelect={toggleSelect} focusedId={focusId}
              onSelect={t => { setFocusId(t.id); setParam('thread', t.id); }} emptyText={activeQ ? 'No mail matches that search' : 'Nothing here'} />
            {next && <button onClick={() => load(true)} disabled={loading} className="w-full py-3 text-xs text-sky-700 hover:text-sky-900 font-semibold" data-testid="mail-load-more">{loading ? 'Loading…' : 'Load more'}</button>}
          </div>
        </section>

        <section className={`${selected ? 'flex' : 'hidden lg:flex'} flex-col ${PANEL} overflow-hidden min-h-0`} data-testid="mail-view-pane">
          {selected ? <MailThreadView key={`${mailbox}:${selected}`} threadId={selected} me={mailbox || user?.email} onChanged={onChanged} onBack={() => setParam('thread', '')} labels={labels} onLabelCreated={onLabelCreated} shortcutRef={threadKeys} />
            : <div className="flex-1 flex flex-col items-center justify-center text-slate-500 text-sm gap-2" data-testid="mail-view-empty"><Mail className="w-10 h-10 text-slate-300" />Pick a conversation<span className="text-[11px] text-slate-400">j / k to move · Enter to open · ? for all shortcuts</span></div>}
        </section>
      </div>
      {compose && <MailComposer draft={{ mode: 'new' }} onClose={(hadDraft) => { setCompose(false); if (hadDraft && folder === 'drafts') setTimeout(() => load(false), 800); }} onSent={() => { if (folder === 'sent') load(false); }} />}
      {help && <ShortcutsHelp onClose={() => setHelp(false)} />}
      {status?.ok && !status.can_send && (
        <p className="mt-2 text-[11px] text-amber-700" data-testid="mail-readonly-footer">Reading works now. To reply, compose, archive and star from ops: {status.action}</p>
      )}
    </div>
  );
}
